import { useAuthMiddleware } from '@/core/middlewares/auth.ts';
import { replayRouteGuard, capturePageview } from '@/core/session-replay';
import { AppScreens } from '@/shared/types';
import { getEnv } from '@/shared/utils/get-env.ts';
import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useLoaderAtStartStore } from '@/shared/components/loaders/LoaderAtStartApp/store.ts';
import { useShareLinkDetector } from '@/shared/composables/useShareLinkDetector';
import { getRedirectLocation } from '@/shared/tools/get-redirect-route';

const otherRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => getRedirectLocation(),
  },
  {
    path: '/icons-preview',
    name: AppScreens.IconsPreview,
    component: () => import('@/views/IconsPreview.vue'),
    meta: {
      layout: 'empty',
    },
  },
  {
    path: `/public-privacy-policy`,
    component: () => import('@/views/PrivacyPolicy.vue'),
    name: 'PublicPrivacyPolicy',
    meta: {
      layout: 'empty',
      public: true,
    },
    beforeEnter() {
      const { visible, loaderIsRequired } = storeToRefs(useLoaderAtStartStore());

      visible.value = false;
      loaderIsRequired.value = false;
    },
  },
  {
    path: `/privacy-policy`,
    component: () => import('@/views/PrivacyPolicy.vue'),
    name: 'PrivacyPolicy',
    meta: {
      layout: 'main',
    },
    beforeEnter() {
      const { visible, loaderIsRequired } = storeToRefs(useLoaderAtStartStore());

      visible.value = false;
      loaderIsRequired.value = false;
    },
  },
  {
    path: `/terms-of-use`,
    component: () => import('@/views/TermsOfUse.vue'),
    name: 'TermsOfUse',
    meta: {
      layout: 'main',
    },
    beforeEnter() {
      const { visible, loaderIsRequired } = storeToRefs(useLoaderAtStartStore());

      visible.value = false;
      loaderIsRequired.value = false;
    },
  },
  {
    path: `/public-terms-of-use`,
    component: () => import('@/views/TermsOfUse.vue'),
    name: 'PublicTermsOfUse',
    meta: {
      layout: 'empty',
      public: true,
    },
    beforeEnter() {
      const { visible, loaderIsRequired } = storeToRefs(useLoaderAtStartStore());

      visible.value = false;
      loaderIsRequired.value = false;
    },
  },
  {
    path: `/about-us`,
    component: () => import('@/views/AboutUs.vue'),
    name: 'AboutUs',
    meta: {
      layout: 'main',
    },
  },
  {
    path: `/leave-feedback`,
    component: () => import('@/views/LeaveFeedback.vue'),
    name: 'LeaveFeedback',
    meta: {
      layout: 'main',
    },
  },
  {
    path: '/courses',
    name: AppScreens.Courses,
    component: () => import('@/views/Courses.vue'),
    meta: {
      layout: 'main',
    },
  },
  {
    path: `/news`,
    component: () => import('@/views/News.vue'),
    name: 'News',
    meta: {
      layout: 'main',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: {
      name: AppScreens.Home,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({
    top: 0,
  }),
  routes: otherRoutes,
});

const { redirectIfShareLinkOnMobile } = useShareLinkDetector();

router.beforeEach(redirectIfShareLinkOnMobile);

const auth = useAuthMiddleware({
  appHttpEndpoint: getEnv('VITE_APP_HTTP'),
});

router.beforeEach(auth);

router.beforeEach(replayRouteGuard);

router.afterEach(() => {
  capturePageview();
});

export default router;
